import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import './Detected.css';


const Detected = () => {


    const { id: businessId } = useParams(); 
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0); // Initialize with 0
    const [citiesData, setCitiesData] = useState([]);
    const [cityTaxData, setCityTaxData] = useState([]);
    const [stateTaxData, setStateTaxData] = useState([]);
    const [narrowedData, setNarrowedData] = useState({
        withholdingStates: {},
        unemploymentStates: {},
        diNeededStates: {},
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cityInputs, setCityInputs] = React.useState({});
    const [stateInputs, setStateInputs] = React.useState({});
    const [federalTaxData, setFederalTaxData] = useState(null);
    const [federalInputs, setFederalInputs] = useState({
        federalId: "",
        filingFrequency: "",
        unemploymentExempt: "",
    });

    

    const allStates = new Set([
        ...Object.keys(narrowedData.withholdingStates),
        ...Object.keys(narrowedData.unemploymentStates),
        ...Object.keys(narrowedData.diNeededStates),
        ...stateTaxData.map((data) => data.state), // Include states from available tax data
    ]);
    
    

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [taxInfoResponse, cityResponse, stateResponse, federalResponse] = await Promise.all([
                    api.get(`/tax-info/${businessId}`),
                    api.get(`/get-city-data/${businessId}`),
                    api.get(`/get-state-data/${businessId}`),
                    api.get(`/get-federal-data/${businessId}`),
                ]);

                const taxInfo = taxInfoResponse.data.taxInfo;

                const withholdingStates = {};
                const unemploymentStates = {};
                const diNeededStates = {};

                setCitiesData(taxInfo.taxableCities || []);
                setCityTaxData(cityResponse.data || []);
                setStateTaxData(stateResponse.data || []);
                setFederalTaxData(federalResponse.data || []);

                Object.entries(taxInfo.groupedEmployees || {}).forEach(([state, workLocs]) => {
                    Object.entries(workLocs).forEach(([workLoc, mandates]) => {
                        Object.entries(mandates).forEach(([mandate, taxRequireds]) => {
                            Object.entries(taxRequireds).forEach(([taxRequired, groupedData]) => {
                                if (groupedData.stateWithholding) {
                                    withholdingStates[groupedData.stateWithholding] =
                                        (withholdingStates[groupedData.stateWithholding] || 0) +
                                        groupedData.count;
                                }
                                if (groupedData.stateUnemployment) {
                                    unemploymentStates[groupedData.stateUnemployment] =
                                        (unemploymentStates[groupedData.stateUnemployment] || 0) +
                                        groupedData.count;
                                }
                                if (groupedData.diNeeded) {
                                    diNeededStates[groupedData.diNeeded] =
                                        (diNeededStates[groupedData.diNeeded] || 0) + groupedData.count;
                                }
                            });
                        });
                    });
                });
                setNarrowedData({ withholdingStates, unemploymentStates, diNeededStates });
            } catch (error) {
                console.error("Error fetching data:", error);
                setError("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [businessId, refreshKey]);

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;



    const handleReturnToPayroll = async () => {
        try {
            const response = await api.get(`/payroll/periods/${businessId}`);
            const data = response.data;
    
            if (data.length === 0) {
                navigate(`/dashboard/business/${businessId}/start-payroll`);
            } else {
                navigate(`/dashboard/business/${businessId}/run-new-pay`);
            }
        } catch (err) {
            console.error("Error fetching pay periods:", err);
            alert("Failed to fetch pay periods. Please try again.");
        }
    };
    


    const handleFederalInputChange = (field, value) => {
        setFederalInputs((prev) => ({
            ...prev,
            [field]: value,
        }));
    };


    const handleFederalSubmit = async () => {
        const { federalId, filingFrequency, unemploymentExempt } = federalInputs;
    
        if (!federalId || !filingFrequency || unemploymentExempt === "") {
            alert("Please fill in all fields.");
            return;
        }
    
        try {
            const response = await api.post(`/federal-tax-info/${businessId}`, {
                federal_id: federalId,
                filing_frequency: filingFrequency,
                unemployment_exempt: unemploymentExempt === "1" ? 1 : 0,
            });
    
            console.log(response.data.message);
            setRefreshKey((prev) => prev + 1); // Refresh after submission
        } catch (error) {
            console.error("Error submitting federal tax info:", error);
            alert(error.response?.data?.error || "Failed to save federal tax information.");
        }
    };
    
    


    const handleInputChange = (cityKey, field, value) => {
        setCityInputs((prev) => ({
            ...prev,
            [cityKey]: {
                ...prev[cityKey],
                [field]: value,
            },
        }));
    };


    const handleCitySubmit = async (cityKey, city) => {
        const inputs = cityInputs[cityKey] || {};
        const { accountNumber, depositFrequency } = inputs;
    
        if (!accountNumber || !depositFrequency) {
            alert("Please fill in all fields.");
            return;
        }
    
        try {
            const response = await api.post(`/city-tax-info/${businessId}`, {
                cityName: city.city_name,
                stateCode: city.state_code,
                accountNumber,
                depositFrequency,
            });
            console.log(response.data.message);
            setRefreshKey((prev) => prev + 1); 
            
        } catch (error) {
            console.error("Error saving city tax info:", error);
            alert(error.response?.data?.error || "Failed to save city tax information.");
        }
    };


    

    



    const handleStateInputChange = (stateKey, field, value) => {
        setStateInputs((prev) => ({
            ...prev,
            [stateKey]: {
                ...prev[stateKey],
                [field]: value,
            },
        }));
    };


    const handleStateSubmit = async (state) => {
        const inputs = stateInputs[state] || {};
        const payload = {
            state,
            withholdingAccountNumber: inputs.withholdingAccountNumber || null,
            uiaAccountNumber: inputs.uiaAccountNumber || null,
            unemploymentTaxRate: inputs.unemploymentTaxRate || null,
            diHandling: inputs.diHandling || null,
            employerRate: inputs.employerRate || null,
        };
    
        console.log("Payload being sent to backend:", payload);
    
        try {
            const response = await api.post(`/state-tax-info/${businessId}`, payload);
            console.log(response.data.message);
            setRefreshKey((prev) => prev + 1); 

        } catch (error) {
            console.error("Error submitting state tax info:", error);
            alert("Failed to save state tax information.");
        }
    };
    

    const hasMissingFederalFields = federalTaxData === null || Object.keys(federalTaxData).length === 0;  // Covers empty object case;



    // Check for any missing fields across all sections
    const hasMissingCityFields = citiesData.some((city) => {
        const availableCity = cityTaxData.find(
            (data) =>
                data.city_name === city.city_name &&
                data.state_code === city.state_code
        );
        return !availableCity;
    });



    const hasMissingStateFields = Array.from(allStates).some((state) => {
        const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
        const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
        const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";
    
        const availableState = stateTaxData.find((data) => data.state === state);
        const withholdingAvailable = availableState?.withholding_account_number || "N/A";
        const unemploymentAvailable = availableState?.uia_account_number || "N/A";
        const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
        const diNeededAvailable = availableState?.istaxing_di ?? "N/A";
        //const diNeededAvailable = availableState?.di_handling || "N/A";
        const diEmployerRateAvailable = availableState?.employer_rate || "N/A";
    
        return (
            (withholdingRequired !== "N/A" && withholdingAvailable === "N/A") ||
            (unemploymentRequired !== "N/A" &&
                (unemploymentAvailable === "N/A" || unemploymentTaxRateAvailable === "N/A")) ||
            (diNeededRequired !== "N/A" && diNeededAvailable === "N/A" && diEmployerRateAvailable === "N/A")
        );
    });

    const noIssues =
    !hasMissingCityFields  && !hasMissingStateFields && !hasMissingFederalFields;



      if (loading) return <p>Loading...</p>;
      if (error) return <p style={{ color: "red" }}>{error}</p>;
           


    return (
        <div className="detect-page">
            <h2 className="detecth2">Tax Information</h2>
            <button className="return-button"  onClick={handleReturnToPayroll}>Return to Payroll</button>

            {noIssues ? (
                <p>All tax information is up to date!</p>
            ) : ( 
                <>
                <p>There is missing tax information detected.</p>



                {/* Federal Tax Information Section */}
<div className="section-container-fed">
    
    {hasMissingFederalFields ? (
        <div className="tax-card-fed">
            <h5>Missing Federal Tax Information</h5>

            <div>
                <label>FEIN / Tax ID:</label>
                <input
                    type="text"
                    placeholder="Enter FEIN / Tax ID"
                    value={federalInputs.federalId}
                    onChange={(e) => handleFederalInputChange("federalId", e.target.value)}
                />
            </div>

            <div>
                <label>Filing Frequency:</label>
                <select
                    value={federalInputs.filingFrequency}
                    onChange={(e) => handleFederalInputChange("filingFrequency", e.target.value)}
                >
                    <option value="">Select Frequency</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annually">Annual</option>
                </select>
            </div>

            <div>
                <label>Unemployment Exempt:</label>
                <select
                    value={federalInputs.unemploymentExempt}
                    onChange={(e) => handleFederalInputChange("unemploymentExempt", e.target.value)}
                >
                    <option value="">Select</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                </select>
            </div>

            <button onClick={handleFederalSubmit}>Submit Federal Tax Info</button>
        </div>
    ) : (
        <p></p>
    )}
</div>




                    {/* Missing City Fields */}
                
                    
                    {citiesData.map((city, index) => {
    const availableCity = cityTaxData.find(
        (data) =>
            data.city_name === city.city_name &&
            data.state_code === city.state_code
    );

    const cityKey = `${city.city_name}_${city.state_code}`; // Unique key for each city
    const inputs = cityInputs[cityKey] || {};
    
    if (!availableCity) {
        
        return (
            <div className="section-container-city">
            <div
                key={index}
                className="tax-card-city"
            >
                <h5>
                    {city.city_name}, {city.state_code} - Fill Missing City Tax Information
                </h5>
                <div>
                    <label>Account Number:</label>
                    <input
                        type="text"
                        placeholder="Enter Account Number"
                        value={inputs.accountNumber || ""}
                        onChange={(e) =>
                            handleInputChange(cityKey, "accountNumber", e.target.value)
                        }
                    />
                </div>
                <div>
                    <label>Deposit Frequency:</label>
                    <select
                        value={inputs.depositFrequency || ""}
                        onChange={(e) =>
                            handleInputChange(cityKey, "depositFrequency", e.target.value)
                        }
                    >
                        <option value="">Select Frequency</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                    </select>
                </div>
                <button onClick={() => handleCitySubmit(cityKey, city)}>
                    Submit City Data
                </button>
            </div>
            </div>
        );
    }
    
    return null;
})}

                    

                   
                    {/* Missing State Fields */}
                    <div className="section-container-state">
                    {Array.from(allStates).map((state) => {
    const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
    const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
    const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

    const availableState = stateTaxData.find((data) => data.state === state);
    const withholdingAvailable = availableState?.withholding_account_number || "N/A";
    const unemploymentAvailable = availableState?.uia_account_number || "N/A";
    const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
    const diNeededAvailable = availableState?.istaxing_di ?? "N/A";
    //const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

    // Check for missing fields
    const missingFields = [];
    if (withholdingRequired !== "N/A" && withholdingAvailable === "N/A") {
        missingFields.push("Withholding Account Number");
    }
    if (
        unemploymentRequired !== "N/A" &&
        (unemploymentAvailable === "N/A" || unemploymentTaxRateAvailable === "N/A")
    ) {
        missingFields.push("UIA Account Number or Unemployment Tax Rate");
    }
    if (
        diNeededRequired !== "N/A" &&
        diNeededAvailable === "N/A"
    ) {
        missingFields.push("DI Handling or Employer Rate");
    }

    const inputs = stateInputs[state] || {};

    return missingFields.length > 0 ? (
        <div
            key={state}
            className="tax-card-state"
        >
            <h5>{state} - Fill Missing State Tax Information</h5>
            {missingFields.includes("Withholding Account Number") && (
                <div>
                    <label>Withholding Account Number:</label>
                    <input
                        type="text"
                        placeholder="Enter Withholding Account Number"
                        value={inputs.withholdingAccountNumber || ""}
                        onChange={(e) =>
                            handleStateInputChange(state, "withholdingAccountNumber", e.target.value)
                        }
                    />
                </div>
            )}
            {missingFields.includes("UIA Account Number or Unemployment Tax Rate") && (
                <div>
                    <label>UIA Account Number:</label>
                    <input
                        type="text"
                        placeholder="Enter UIA Account Number"
                        value={inputs.uiaAccountNumber || ""}
                        onChange={(e) =>
                            handleStateInputChange(state, "uiaAccountNumber", e.target.value)
                        }
                    />
                    <label>Unemployment Tax Rate:</label>
                    <input
                        type="text"
                        placeholder="Enter Unemployment Tax Rate"
                        value={inputs.unemploymentTaxRate || ""}
                        onChange={(e) =>
                            handleStateInputChange(state, "unemploymentTaxRate", e.target.value)
                        }
                    />
                </div>
            )}
            
            {missingFields.includes("DI Handling or Employer Rate") && (
    <div>
        <label>
            {state === "NY"
                ? "Does the business charge employees 0.5% capped at 0.60 cents per week?"
                : state === "NJ"
                ? "DI Handling:"
                : "Does the business deduct for DI insurance from employees' pay?"}
        </label>

        {/* NY: Yes/No Dropdown */}
        {state === "NY" && (
            <select
                value={inputs.diHandling || ""}
                onChange={(e) =>
                    handleStateInputChange(state, "diHandling", e.target.value)
                }
            >
                <option value="">Select</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
            </select>
        )}

        {/* NJ: DI Handling Dropdown */}
        {state === "NJ" && (
            <>
                <select
                    value={inputs.diHandling || ""}
                    onChange={(e) =>
                        handleStateInputChange(state, "diHandling",e.target.value)
                    }
                >
                    <option value="">Select</option>
                    <option value="1">Employer withdraws for DI insurance</option>
                    <option value="0">Employer does not withdraw for DI insurance</option>
                </select>

                {/* Show Employer Rate only if Withdraw is selected */}
                {inputs.diHandling === "1" && (
                    <div>
                        <label>Employer Rate (%):</label>
                        <input
                            type="text"
                            placeholder="Enter Employer Rate"
                            value={inputs.employerRate || ""}
                            onChange={(e) =>
                                handleStateInputChange(state, "employerRate", e.target.value)
                            }
                        />
                    </div>
                )}
            </>
        )}

        {/* Other States: Yes/No Dropdown */}
        {state !== "NY" && state !== "NJ" && (
            <select
                value={inputs.diHandling || ""}
                onChange={(e) =>
                    handleStateInputChange(state, "diHandling", e.target.value)
                }
            >
                <option value="">Select</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
            </select>
                )}
            </div>
        )}

            <button onClick={() => handleStateSubmit(state)}>Submit State Data</button>
        </div>
    ) : null;
})}
</div>

                </>
            )}
        </div>
    );
};

export default Detected; 






// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";

// const Detected = () => {
//     const { id: businessId } = useParams();

//     const [citiesData, setCitiesData] = useState([]);
//     const [cityTaxData, setCityTaxData] = useState([]);
//     const [taxableCounties, setTaxableCounties] = useState([]);
//     const [countyTaxData, setCountyTaxData] = useState([]);
//     const [stateTaxData, setStateTaxData] = useState([]);
//     const [narrowedData, setNarrowedData] = useState({
//         withholdingStates: {},
//         unemploymentStates: {},
//         diNeededStates: {},
//     });
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);

//     useEffect(() => {
//         const fetchAllData = async () => {
//             try {
//                 const [taxInfoResponse, cityResponse, countyResponse, stateResponse] = await Promise.all([
//                     api.get(`/tax-info/${businessId}`),
//                     api.get(`/get-city-data/${businessId}`),
//                     api.get(`/get-county-data/${businessId}`),
//                     api.get(`/get-state-data/${businessId}`),
//                 ]);

//                 const taxInfo = taxInfoResponse.data.taxInfo;

//                 const withholdingStates = {};
//                 const unemploymentStates = {};
//                 const diNeededStates = {};

//                 setCitiesData(taxInfo.taxableCities || []);
//                 setTaxableCounties(taxInfo.taxableCounties || []);
//                 setCityTaxData(cityResponse.data || []);
//                 setCountyTaxData(countyResponse.data || []);
//                 setStateTaxData(stateResponse.data || []);

//                 Object.entries(taxInfo.groupedEmployees || {}).forEach(([state, workLocs]) => {
//                     Object.entries(workLocs).forEach(([workLoc, mandates]) => {
//                         Object.entries(mandates).forEach(([mandate, taxRequireds]) => {
//                             Object.entries(taxRequireds).forEach(([taxRequired, groupedData]) => {
//                                 if (groupedData.stateWithholding) {
//                                     withholdingStates[groupedData.stateWithholding] =
//                                         (withholdingStates[groupedData.stateWithholding] || 0) +
//                                         groupedData.count;
//                                 }
//                                 if (groupedData.stateUnemployment) {
//                                     unemploymentStates[groupedData.stateUnemployment] =
//                                         (unemploymentStates[groupedData.stateUnemployment] || 0) +
//                                         groupedData.count;
//                                 }
//                                 if (groupedData.diNeeded) {
//                                     diNeededStates[groupedData.diNeeded] =
//                                         (diNeededStates[groupedData.diNeeded] || 0) + groupedData.count;
//                                 }
//                             });
//                         });
//                     });
//                 });
//                 setNarrowedData({ withholdingStates, unemploymentStates, diNeededStates });
//             } catch (error) {
//                 console.error("Error fetching data:", error);
//                 setError("Failed to fetch data.");
//             } finally {
//                 setLoading(false);
//             }
//         };

//         fetchAllData();
//     }, [businessId]);

//     if (loading) return <p>Loading...</p>;
//     if (error) return <p style={{ color: "red" }}>{error}</p>;

//     return (
//         <div style={{ padding: "20px" }}>
//             <h2>Detected Issues</h2>

//             {/* City Tax Comparison */}
//             <div>
//                 <h3>City Tax Comparison</h3>
//                 {citiesData.length > 0 ? (
//                     <>
//                         <table>
//                             <thead>
//                                 <tr>
//                                     <th>Required City</th>
//                                     <th>Required State</th>
//                                     <th>Available City</th>
//                                     <th>Available State</th>
//                                     <th>Match Status</th>
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {citiesData.map((city, index) => {
//                                     const availableCity = cityTaxData.find(
//                                         (data) =>
//                                             data.city_name === city.city_name &&
//                                             data.state_code === city.state_code
//                                     );

//                                     const isMatch = availableCity !== undefined;

//                                     return (
//                                         <tr key={index}>
//                                             <td>{city.city_name}</td>
//                                             <td>{city.state_code}</td>
//                                             <td>{availableCity?.city_name || "N/A"}</td>
//                                             <td>{availableCity?.state_code || "N/A"}</td>
//                                             <td style={{ textAlign: "center" }}>
//                                                 {isMatch ? (
//                                                     <span style={{ color: "green" }}>✔️</span>
//                                                 ) : (
//                                                     <span style={{ color: "red" }}>❌</span>
//                                                 )}
//                                             </td>
//                                         </tr>
//                                     );
//                                 })}
//                             </tbody>
//                         </table>

//                         {/* Missing Fields Form for Mismatched Cities */}
//                         {citiesData.map((city, index) => {
//                             const availableCity = cityTaxData.find(
//                                 (data) =>
//                                     data.city_name === city.city_name &&
//                                     data.state_code === city.state_code
//                             );

//                             if (!availableCity) {
//                                 return (
//                                     <div
//                                         key={index}
//                                         style={{
//                                             margin: "10px 0",
//                                             border: "1px solid #ccc",
//                                             padding: "10px",
//                                         }}
//                                     >
//                                         <h5>
//                                             {city.city_name}, {city.state_code} - Fill Missing City Tax Information
//                                         </h5>
//                                         <div>
//                                             <label>Account Number:</label>
//                                             <input
//                                                 type="text"
//                                                 placeholder="Enter Account Number"
//                                             />
//                                         </div>
//                                         <div>
//                                             <label>Deposit Frequency:</label>
//                                             <select>
//                                                 <option>Select Frequency</option>
//                                                 <option>Monthly</option>
//                                                 <option>Quarterly</option>
//                                             </select>
//                                         </div>
//                                     </div>
//                                 );
//                             }
//                             return null;
//                         })}
//                     </>
//                 ) : (
//                     <p>No city tax data to compare.</p>
//                 )}
//             </div>

//             {/* County Tax Comparison */}
//             <div>
//                 <h3>County Tax Comparison</h3>
//                 {taxableCounties.length > 0 ? (
//                     <>
//                         <table>
//                             <thead>
//                                 <tr>
//                                     <th>Required County</th>
//                                     <th>Required State</th>
//                                     <th>Available County</th>
//                                     <th>Available State</th>
//                                     <th>Available Account</th>
//                                     <th>Match Status</th>
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {taxableCounties.flatMap((countyData) =>
//                                     countyData.counties.map((countyKey, index) => {
//                                         const [state, county] = countyKey.split("_");
//                                         const availableCounty = countyTaxData.find(
//                                             (data) =>
//                                                 data.county_name === county &&
//                                                 data.county_state === state
//                                         );

//                                         const isMatch = availableCounty !== undefined;

//                                         return (
//                                             <tr key={`${countyKey}-${index}`}>
//                                                 <td>{county}</td>
//                                                 <td>{state}</td>
//                                                 <td>{availableCounty?.county_name || "N/A"}</td>
//                                                 <td>{availableCounty?.county_state || "N/A"}</td>
//                                                 <td>{availableCounty?.account_number || "N/A"}</td>
//                                                 <td style={{ textAlign: "center" }}>
//                                                     {isMatch ? (
//                                                         <span style={{ color: "green" }}>✔️</span>
//                                                     ) : (
//                                                         <span style={{ color: "red" }}>❌</span>
//                                                     )}
//                                                 </td>
//                                             </tr>
//                                         );
//                                     })
//                                 )}
//                             </tbody>
//                         </table>

//                         {/* Missing Fields Form for Mismatched Counties */}
//                         {taxableCounties.flatMap((countyData) =>
//                             countyData.counties.map((countyKey, index) => {
//                                 const [state, county] = countyKey.split("_");
//                                 const availableCounty = countyTaxData.find(
//                                     (data) =>
//                                         data.county_name === county &&
//                                         data.county_state === state
//                                 );

//                                 if (!availableCounty) {
//                                     return (
//                                         <div
//                                             key={`${countyKey}-${index}`}
//                                             style={{
//                                                 margin: "10px 0",
//                                                 border: "1px solid #ccc",
//                                                 padding: "10px",
//                                             }}
//                                         >
//                                             <h5>
//                                                 {county}, {state} - Fill Missing County Tax Information
//                                             </h5>
//                                             <div>
//                                                 <label>County Tax Account Number:</label>
//                                                 <input
//                                                     type="text"
//                                                     placeholder="Enter County Tax Account Number"
//                                                 />
//                                             </div>
//                                         </div>
//                                     );
//                                 }
//                                 return null;
//                             })
//                         )}
//                     </>
//                 ) : (
//                     <p>No county tax data to compare.</p>
//                 )}
//             </div>

//             {/* State Tax Comparison */}
//             <div>
//                 <h3>State Tax Comparison</h3>
//                 <table>
//                     <thead>
//                         <tr>
//                             <th>State</th>
//                             <th>Withholding States (Required)</th>
//                             <th>Withholding States (Available)</th>
//                             <th>Withholding Status</th>
//                             <th>Unemployment States (Required)</th>
//                             <th>Unemployment States (Available)</th>
//                             <th>Available Unemployment Tax Rate</th>
//                             <th>Unemployment Status</th>
//                             <th>DI Needed (Required)</th>
//                             <th>DI Needed (Available)</th>
//                             <th>Available DI Employer Rate</th>
//                             <th>DI Status</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {Object.keys(narrowedData.withholdingStates).map((state) => {
//                             const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
//                             const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
//                             const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

//                             const availableState = stateTaxData.find((data) => data.state === state);
//                             const withholdingAvailable = availableState?.withholding_account_number || "N/A";
//                             const unemploymentAvailable = availableState?.uia_account_number || "N/A";
//                             const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
//                             const diNeededAvailable = availableState?.di_handling || "N/A";
//                             const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

//                             const withholdingStatus =
//                                 withholdingRequired === "N/A" || withholdingAvailable !== "N/A"
//                                     ? <span style={{ color: "green" }}>✔️</span>
//                                     : <span style={{ color: "red" }}>❌</span>;

//                             const unemploymentStatus =
//                                 unemploymentRequired === "N/A" ||
//                                 (unemploymentAvailable !== "N/A" && unemploymentTaxRateAvailable !== "N/A")
//                                     ? <span style={{ color: "green" }}>✔️</span>
//                                     : <span style={{ color: "red" }}>❌</span>;

//                             const diStatus =
//                                 diNeededRequired === "N/A" ||
//                                 (diNeededAvailable !== "N/A" || diEmployerRateAvailable !== "N/A")
//                                     ? <span style={{ color: "green" }}>✔️</span>
//                                     : <span style={{ color: "red" }}>❌</span>;

//                             return (
//                                 <tr key={state}>
//                                     <td>{state}</td>
//                                     <td>{withholdingRequired}</td>
//                                     <td>{withholdingAvailable}</td>
//                                     <td>{withholdingStatus}</td>
//                                     <td>{unemploymentRequired}</td>
//                                     <td>{unemploymentAvailable}</td>
//                                     <td>{unemploymentTaxRateAvailable}</td>
//                                     <td>{unemploymentStatus}</td>
//                                     <td>{diNeededRequired}</td>
//                                     <td>{diNeededAvailable}</td>
//                                     <td>{diEmployerRateAvailable}</td>
//                                     <td>{diStatus}</td>
//                                 </tr>
//                             );
//                         })}
//                     </tbody>
//                 </table>

//                 {/* Missing Fields Form for Mismatched States */}
//                 {Object.keys(narrowedData.withholdingStates).map((state) => {
//                     const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
//                     const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
//                     const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

//                     const availableState = stateTaxData.find((data) => data.state === state);
//                     const withholdingAvailable = availableState?.withholding_account_number || "N/A";
//                     const unemploymentAvailable = availableState?.uia_account_number || "N/A";
//                     const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
//                     const diNeededAvailable = availableState?.di_handling || "N/A";
//                     const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

//                     const missingFields = [];
//                     if (withholdingRequired !== "N/A" && withholdingAvailable === "N/A") {
//                         missingFields.push("Withholding Account Number");
//                     }
//                     if (
//                         unemploymentRequired !== "N/A" &&
//                         (unemploymentAvailable === "N/A" || unemploymentTaxRateAvailable === "N/A")
//                     ) {
//                         missingFields.push("UIA Account Number or Unemployment Tax Rate");
//                     }
//                     if (
//                         diNeededRequired !== "N/A" &&
//                         diNeededAvailable === "N/A" &&
//                         diEmployerRateAvailable === "N/A"
//                     ) {
//                         missingFields.push("DI Handling or Employer Rate");
//                     }

//                     return missingFields.length > 0 ? (
//                         <div
//                             key={state}
//                             style={{
//                                 margin: "10px 0",
//                                 border: "1px solid #ccc",
//                                 padding: "10px",
//                             }}
//                         >
//                             <h5>{state} - Fill Missing State Tax Information</h5>
//                             {missingFields.includes("Withholding Account Number") && (
//                                 <div>
//                                     <label>Withholding Account Number:</label>
//                                     <input type="text" placeholder="Enter Withholding Account Number" />
//                                 </div>
//                             )}
//                             {missingFields.includes("UIA Account Number or Unemployment Tax Rate") && (
//                                 <div>
//                                     <label>UIA Account Number:</label>
//                                     <input type="text" placeholder="Enter UIA Account Number" />
//                                     <label>Unemployment Tax Rate:</label>
//                                     <input type="text" placeholder="Enter Unemployment Tax Rate" />
//                                 </div>
//                             )}
//                             {missingFields.includes("DI Handling or Employer Rate") && (
//                                 <div>
//                                     <label>DI Handling:</label>
//                                     <select>
//                                         <option>Select Handling</option>
//                                         <option>Employer Withdraws for DI Insurance</option>
//                                         <option>State Handles DI Insurance</option>
//                                     </select>
//                                     <label>Employer Rate (%):</label>
//                                     <input type="text" placeholder="Enter Employer Rate" />
//                                 </div>
//                             )}
//                         </div>
//                     ) : null;
//                 })}
//             </div>
//         </div>
//     );
// };

// export default Detected;


// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";

// const Detected = () => {
//     const { id: businessId } = useParams();

//     const [citiesData, setCitiesData] = useState([]);
//     const [cityTaxData, setCityTaxData] = useState([]);
//     const [taxableCounties, setTaxableCounties] = useState([]);
//     const [countyTaxData, setCountyTaxData] = useState([]);
//     const [stateTaxData, setStateTaxData] = useState([]);
//     const [narrowedData, setNarrowedData] = useState({
//         withholdingStates: {},
//         unemploymentStates: {},
//         diNeededStates: {},
//     });
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);

//     useEffect(() => {
//         const fetchAllData = async () => {
//             try {
//                 const [taxInfoResponse, cityResponse, countyResponse, stateResponse] = await Promise.all([
//                     api.get(`/tax-info/${businessId}`), // Fetch main tax info
//                     api.get(`/get-city-data/${businessId}`), // Fetch city tax info
//                     api.get(`/get-county-data/${businessId}`), // Fetch county tax info
//                     api.get(`/get-state-data/${businessId}`), // Fetch state tax info
//                 ]);

//                 const taxInfo = taxInfoResponse.data.taxInfo;

//                 const withholdingStates = {};
//                 const unemploymentStates = {};
//                 const diNeededStates = {};

//                 setCitiesData(taxInfo.taxableCities || []);
//                 setTaxableCounties(taxInfo.taxableCounties || []);
//                 setCityTaxData(cityResponse.data || []);
//                 setCountyTaxData(countyResponse.data || []);
//                 setStateTaxData(stateResponse.data || []);

//                 Object.entries(taxInfo.groupedEmployees || {}).forEach(([state, workLocs]) => {
//                     Object.entries(workLocs).forEach(([workLoc, mandates]) => {
//                         Object.entries(mandates).forEach(([mandate, taxRequireds]) => {
//                             Object.entries(taxRequireds).forEach(([taxRequired, groupedData]) => {
//                                 if (groupedData.stateWithholding) {
//                                     withholdingStates[groupedData.stateWithholding] =
//                                         (withholdingStates[groupedData.stateWithholding] || 0) +
//                                         groupedData.count;
//                                 }
//                                 if (groupedData.stateUnemployment) {
//                                     unemploymentStates[groupedData.stateUnemployment] =
//                                         (unemploymentStates[groupedData.stateUnemployment] || 0) +
//                                         groupedData.count;
//                                 }
//                                 if (groupedData.diNeeded) {
//                                     diNeededStates[groupedData.diNeeded] =
//                                         (diNeededStates[groupedData.diNeeded] || 0) + groupedData.count;
//                                 }
//                             });
//                         });
//                     });
//                 });
//                 setNarrowedData({ withholdingStates, unemploymentStates, diNeededStates });
//             } catch (error) {
//                 console.error("Error fetching data:", error);
//                 setError("Failed to fetch data.");
//             } finally {
//                 setLoading(false);
//             }
//         };

//         fetchAllData();
//     }, [businessId]);

//     if (loading) return <p>Loading...</p>;
//     if (error) return <p style={{ color: "red" }}>{error}</p>;

//     return (
//         <div style={{ padding: "20px" }}>
//             <h2>Detected Issues</h2>

//             {/* City Tax Comparison */}
//             <div>
//                 <h3>City Tax Comparison</h3>
//                 {citiesData.length > 0 ? (
//                     <table>
//                         <thead>
//                             <tr>
//                                 <th>Required City</th>
//                                 <th>Required State</th>
//                                 <th>Available City</th>
//                                 <th>Available State</th>
//                                 <th>Match Status</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {citiesData.map((city, index) => {
//                                 const availableCity = cityTaxData.find(
//                                     (data) =>
//                                         data.city_name === city.city_name &&
//                                         data.state_code === city.state_code
//                                 );

//                                 const isMatch = availableCity !== undefined;

//                                 return (
//                                     <tr key={index}>
//                                         <td>{city.city_name}</td>
//                                         <td>{city.state_code}</td>
//                                         <td>{availableCity?.city_name || "N/A"}</td>
//                                         <td>{availableCity?.state_code || "N/A"}</td>
//                                         <td style={{ textAlign: "center" }}>
//                                             {isMatch ? (
//                                                 <span style={{ color: "green" }}>✔️</span>
//                                             ) : (
//                                                 <span style={{ color: "red" }}>❌</span>
//                                             )}
//                                         </td>
//                                     </tr>
//                                 );
//                             })}
//                         </tbody>
//                     </table>
//                 ) : (
//                     <p>No city tax data to compare.</p>
//                 )}
//             </div>

//             {/* County Tax Comparison */}
//             <div>
//                 <h3>County Tax Comparison</h3>
//                 {taxableCounties.length > 0 ? (
//                     <table>
//                         <thead>
//                             <tr>
//                                 <th>Required County</th>
//                                 <th>Required State</th>
//                                 <th>Available County</th>
//                                 <th>Available State</th>
//                                 <th>Available Account</th>
//                                 <th>Match Status</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {taxableCounties.flatMap((countyData) =>
//                                 countyData.counties.map((countyKey, index) => {
//                                     const [state, county] = countyKey.split("_");
//                                     const availableCounty = countyTaxData.find(
//                                         (data) =>
//                                             data.county_name === county &&
//                                             data.county_state === state
//                                     );

//                                     const isMatch = availableCounty !== undefined;

//                                     return (
//                                         <tr key={`${countyKey}-${index}`}>
//                                             <td>{county}</td>
//                                             <td>{state}</td>
//                                             <td>{availableCounty?.county_name || "N/A"}</td>
//                                             <td>{availableCounty?.county_state || "N/A"}</td>
//                                             <td>{availableCounty?.account_number || "N/A"}</td>
//                                             <td style={{ textAlign: "center" }}>
//                                                 {isMatch ? (
//                                                     <span style={{ color: "green" }}>✔️</span>
//                                                 ) : (
//                                                     <span style={{ color: "red" }}>❌</span>
//                                                 )}
//                                             </td>
//                                         </tr>
//                                     );
//                                 })
//                             )}
//                         </tbody>
//                     </table>
//                 ) : (
//                     <p>No county tax data to compare.</p>
//                 )}
//             </div>

//             {/* State Tax Comparison */}
//             <div>
//                 <h3>State Tax Comparison</h3>
//                 <table>
//                     <thead>
//                         <tr>
//                             <th>State</th>
//                             <th>Withholding States (Required)</th>
//                             <th>Withholding States (Available)</th>
//                             <th>Withholding Status</th>
//                             <th>Unemployment States (Required)</th>
//                             <th>Unemployment States (Available)</th>
//                             <th>Available Unemployment Tax Rate</th>
//                             <th>Unemployment Status</th>
//                             <th>DI Needed (Required)</th>
//                             <th>DI Needed (Available)</th>
//                             <th>Available DI Employer Rate</th>
//                             <th>DI Status</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {Object.keys(narrowedData.withholdingStates).map((state) => {
//                             const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
//                             const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
//                             const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

//                             const availableState = stateTaxData.find((data) => data.state === state);
//                             const withholdingAvailable = availableState?.withholding_account_number || "N/A";
//                             const unemploymentAvailable = availableState?.uia_account_number || "N/A";
//                             const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
//                             const diNeededAvailable = availableState?.di_handling || "N/A";
//                             const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

//                             const withholdingStatus =
//                                 withholdingRequired === "N/A" || withholdingAvailable !== "N/A"
//                                     ? <span style={{ color: "green" }}>✔️</span>
//                                     : <span style={{ color: "red" }}>❌</span>;

//                             const unemploymentStatus =
//                                 unemploymentRequired === "N/A" ||
//                                 (unemploymentAvailable !== "N/A" && unemploymentTaxRateAvailable !== "N/A")
//                                     ? <span style={{ color: "green" }}>✔️</span>
//                                     : <span style={{ color: "red" }}>❌</span>;

//                             const diStatus =
//                                 diNeededRequired === "N/A" ||
//                                 (diNeededAvailable !== "N/A" || diEmployerRateAvailable !== "N/A")
//                                     ? <span style={{ color: "green" }}>✔️</span>
//                                     : <span style={{ color: "red" }}>❌</span>;

//                             return (
//                                 <tr key={state}>
//                                     <td>{state}</td>
//                                     <td>{withholdingRequired}</td>
//                                     <td>{withholdingAvailable}</td>
//                                     <td>{withholdingStatus}</td>
//                                     <td>{unemploymentRequired}</td>
//                                     <td>{unemploymentAvailable}</td>
//                                     <td>{unemploymentTaxRateAvailable}</td>
//                                     <td>{unemploymentStatus}</td>
//                                     <td>{diNeededRequired}</td>
//                                     <td>{diNeededAvailable}</td>
//                                     <td>{diEmployerRateAvailable}</td>
//                                     <td>{diStatus}</td>
//                                 </tr>
//                             );
//                         })}
//                     </tbody>
//                 </table>
//             </div>
//         </div>
//     );
// };

// export default Detected;
